import React, { useState, useMemo, useEffect } from 'react';
import { ReanalysisRequest, AttachmentInfo, OcrDecision } from '../types';
import {
  indexOcrDecisionsByName, ocrReasonLabel, ocrReasonGroupHeading, resolveAttachmentOcrRows,
  partitionAttachmentRows, countAttachments, AttachmentOcrRow,
} from '../services/attachmentOcrStatus';
import { TriStateControl, SkeletonBlock } from './shared';
import { OCR_OPTIONS, ASSIGNABLE_CATEGORIES } from '../constants';
import { theme } from '../theme';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';

interface ReanalysisModalProps {
  ticketUuid: string;
  currentCategory?: string;
  reanalysisCount?: number;
  reanalysisBlocked?: boolean;
  /** The row is SETTLED (approved or declined — Entschieden; Genehmigen settles the row, 2026-10-23):
   *  the re-run takes the standing decision back, so the reason is mandatory whatever the attempt
   *  number says, and the modal says so. Core refuses without one (ReasonRequired). */
  settled?: boolean;
  attachments?: AttachmentInfo[];
  ocrDecisions?: OcrDecision[];
  onSubmit: (request: ReanalysisRequest) => void;
  onUnblock?: () => void;
  onClose: () => void;
  isSubmitting: boolean;
  isAdmin?: boolean;
  // Tenant setting (operator_config UI_ATTACHMENTS_COUNT_INLINE via /config) — this modal is
  // always hosted inside a surface that already threads the flag from App's single /config read
  // (its only two hosts are AntwortenSeite and PopoutShell, both of which render it over a
  // TicketDetail that got the same value); it must never read config itself. ABSENT ⇒ true,
  // matching TicketDetail's own default so an omitted prop reproduces pre-existing behaviour.
  attachmentsCountInline?: boolean;
}

// gMSB fallback option list (unchanged when no schema)
const GMSB_CATEGORIES = ASSIGNABLE_CATEGORIES;

/** Map pipeline ocrDecisions → initial tri-state selections */
export const buildInitialOcrOverrides = (
  attachments: AttachmentInfo[] | undefined,
  decisions: OcrDecision[] | undefined,
): Record<string, 'force' | 'auto' | 'skip'> => {
  if (!attachments?.length) return {};
  const map: Record<string, 'force' | 'auto' | 'skip'> = {};
  const decisionMap = indexOcrDecisionsByName(decisions);
  for (const att of attachments) {
    const d = decisionMap.get(att.name);
    if (!d) { map[att.name] = 'auto'; continue; }
    if (d.decidedBy === 'rep') map[att.name] = d.ocr ? 'force' : 'skip';
    // decidedBy names WHO decided; ocr alone says whether the file went through OCR. A system
    // NON-read (inline image / unsupported format) is the only locked skip — a system read
    // (empty-body auto-promotion, forced full OCR) is exactly what 'auto' already means.
    else if (d.decidedBy === 'system') map[att.name] = d.ocr ? 'auto' : 'skip';
    else map[att.name] = 'auto';
  }
  return map;
};

const ReanalysisModal: React.FC<ReanalysisModalProps> = ({
  ticketUuid,
  currentCategory,
  reanalysisCount = 0,
  reanalysisBlocked = false,
  settled = false,
  attachments,
  ocrDecisions,
  onSubmit,
  onUnblock,
  onClose,
  isSubmitting,
  isAdmin = false,
  attachmentsCountInline = true,
}) => {
  const cm = useCategoryMeta();
  // VNB: marktprozess ids from schema; gMSB: K0-K4 assignable codes.
  // performanceCategoryKeys, not categoryKeys — an operator forcing a category is asserting a real
  // classification outcome, and the unclassified bucket ("could not be classified") is never one of those; the
  // legacy arm below has always said the same thing via ASSIGNABLE_CATEGORIES. ⚠ NAMED CONFLATION:
  // the served signal that identifies the unclassified bucket is `excludedFromEvaluation`, an EVALUATION marker
  // being read here as an ASSIGNABILITY one. They coincide for every axis served today (an unclassified value is
  // both), and no second marker exists on the wire — so this reads the one that does exist rather
  // than re-deriving the fact locally. If a tenant ever needs a value that is assignable but not
  // evaluated (or the reverse), that is the moment to split the marker, wire-side first.
  const categoryOptions = cm.categoryLabelsFromData
    ? cm.performanceCategoryKeys.map(id => ({ id, label: cm.label(id) }))
    : GMSB_CATEGORIES.map(id => ({ id, label: id }));
  const [notes, setNotes] = useState('');
  const [suggestedCategory, setSuggestedCategory] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [forceCategory, setForceCategory] = useState(false);
  // Why: default expanded when attachments exist — prevents CS reps missing OCR controls
  const [ocrExpanded, setOcrExpanded] = useState((attachments?.length ?? 0) > 0);
  const initialOcr = useMemo(() => buildInitialOcrOverrides(attachments, ocrDecisions), [attachments, ocrDecisions]);
  const [ocrOverrides, setOcrOverrides] = useState<Record<string, 'force' | 'auto' | 'skip'>>(initialOcr);
  // Why: sync overrides when props change (e.g., after unblock re-fetches ticket detail)
  useEffect(() => { setOcrOverrides(buildInitialOcrOverrides(attachments, ocrDecisions)); }, [attachments, ocrDecisions]);
  // Same join resolveAttachmentOcrRows always uses (attachmentOcrStatus.ts's ONE index) — replaces
  // a second, hand-rolled Map that used to re-derive the identical name→decision fact here.
  const anhaengeRows = useMemo(() => resolveAttachmentOcrRows(attachments, ocrDecisions), [attachments, ocrDecisions]);
  const { documents: anhaengeDocuments, inline: anhaengeInline } = useMemo(
    () => partitionAttachmentRows(anhaengeRows), [anhaengeRows],
  );
  // ON renders the untouched resolved row array — never [...documents, ...inline], which would
  // silently reorder a row whose OCR-decision fallback (isInlineAttachment) disagrees with its
  // position in the raw list. OFF is documents-only; the subheader+inline rows render separately.
  const anhaengeVisibleRows = attachmentsCountInline ? anhaengeRows : anhaengeDocuments;
  const anhaengeCount = countAttachments(anhaengeRows, attachmentsCountInline);
  // Shared row renderer — same JSX regardless of which bucket (documents/inline) or posture
  // (ON/OFF) a row is drawn from, so the control itself can never differ across the split.
  const renderAnhangRow = (row: AttachmentOcrRow) => {
    const isSystem = row.decidedBy === 'system';
    // Only a system NON-read (inline image / unsupported format) is a locked skip — a system read
    // (empty-body auto-promotion, forced full OCR) gets the normal tri-state control like any
    // other 'auto'-seeded attachment.
    const isSystemLockedSkip = isSystem && row.state === 'not_read';
    const isAiDecided = row.decidedBy === 'ai';
    return (
      <div key={row.name} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.sm,
      }}>
        <span style={{
          fontSize: 12, color: theme.colors.navyMid, flex: 1, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {row.name}
          {isAiDecided && (
            <span style={{ fontSize: 10, color: theme.colors.navyMuted, marginLeft: 4 }}>KI</span>
          )}
          {isSystem && row.reason && (
            <span style={{ fontSize: 10, color: theme.colors.textSecondary, marginLeft: 4 }}>
              ({ocrReasonLabel(row.reason)})
            </span>
          )}
        </span>
        {isSystemLockedSkip ? (
          <span style={{ fontSize: 11, color: theme.colors.textSecondary, fontStyle: 'italic' }}>
            Skip
          </span>
        ) : (
          <TriStateControl
            options={[...OCR_OPTIONS]}
            selected={ocrOverrides[row.name] || 'auto'}
            onChange={v => setOcrOverrides(prev => ({ ...prev, [row.name]: v as 'force' | 'auto' | 'skip' }))}
          />
        )}
      </div>
    );
  };

  const attemptNumber = reanalysisCount + 1;
  const hasNotes = notes.trim().length > 0;
  const hasCategory = suggestedCategory.length > 0;
  const hasReason = rejectionReason.length > 0;

  // Progressive gating — and on a settled row the reason is mandatory on top of it.
  const meetsRequirements = (!settled || hasReason) && (attemptNumber >= 3
    ? (hasNotes && hasCategory && hasReason)
    : attemptNumber >= 2
      ? (hasNotes || hasCategory || hasReason)
      : true);

  const handleSubmit = () => {
    // Compare against initialOcr (the SEEDED state), not the literal 'auto' — buildInitialOcrOverrides
    // pre-fills a system non-read as 'skip' so the locked UI paints correctly, and that seed is not a
    // clerk's choice. Filtering on `!== 'auto'` submitted the seed as though the clerk picked it,
    // re-attributing a system OCR decision to 'rep' on every untouched reanalysis (self-perpetuating,
    // since a 'rep' decision seeds right back to 'skip' next time). Only what the clerk actually moved
    // belongs in the payload — including a move TO 'auto', which the old filter silently dropped.
    const changedOverrides = Object.entries(ocrOverrides)
      .filter(([filename, v]) => v !== (initialOcr[filename] ?? 'auto'))
      .map(([filename, decision]) => ({ filename, decision }));

    onSubmit({
      notes: notes.trim() || undefined,
      suggestedPrimaryAxis: suggestedCategory || undefined,
      rejectionReason: rejectionReason.trim() || undefined,
      forcePrimaryAxis: forceCategory && hasCategory ? true : undefined,
      ocrOverrides: changedOverrides.length > 0 ? changedOverrides : undefined,
    });
  };

  if (reanalysisBlocked) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, fontFamily: theme.font.family,
      }}>
        <div style={{
          backgroundColor: theme.colors.white,
          borderRadius: theme.borderRadius.lg,
          padding: theme.spacing.lg,
          width: '90%', maxWidth: 500,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}>
          <h2 style={{ margin: `0 0 ${theme.spacing.md}px 0`, fontSize: 18, fontWeight: 600, color: theme.colors.navy }}>
            Neuanalyse blockiert
          </h2>
          <p style={{ fontSize: 13, color: theme.colors.navyMid, marginBottom: theme.spacing.lg }}>
            {isAdmin
              ? 'Dieses Ticket wurde nach 3 Neuanalysen gesperrt. Sie können die Sperre als Administrator aufheben.'
              : 'Dieses Ticket wurde nach 3 Neuanalysen gesperrt. Bitte wenden Sie sich an einen Administrator.'}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
            {isAdmin && onUnblock && (
              <button
                onClick={onUnblock}
                disabled={isSubmitting}
                style={{
                  padding: `${theme.spacing.sm + 2}px ${theme.spacing.lg}px`,
                  borderRadius: theme.borderRadius.md,
                  border: 'none',
                  backgroundColor: theme.colors.navy,
                  color: theme.colors.white,
                  cursor: isSubmitting ? 'wait' : 'pointer',
                  fontSize: 13, fontFamily: theme.font.family,
                  opacity: isSubmitting ? 0.7 : 1,
                }}
              >
                {isSubmitting ? 'Wird entsperrt...' : 'Entsperren'}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                padding: `${theme.spacing.sm + 2}px ${theme.spacing.lg}px`,
                borderRadius: theme.borderRadius.md,
                border: `1px solid ${theme.colors.borderLight}`,
                backgroundColor: theme.colors.white,
                color: theme.colors.navyMid,
                cursor: 'pointer', fontSize: 13, fontFamily: theme.font.family,
              }}
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    );
  }

  const requirementHint = settled
    ? 'Der Vorgang ist bereits entschieden. Die Neuanalyse nimmt die Entscheidung zurück; der Grund ist Pflicht.'
    : attemptNumber >= 3
      ? 'Alle Felder erforderlich für dritte Neuanalyse'
      : attemptNumber >= 2
        ? 'Bitte mindestens ein Feld ausfüllen'
        : undefined;

  const labelSuffix = (required: boolean) => required ? ' *' : ' (optional)';
  const reasonRequired = settled || attemptNumber >= 3;
  const categoryRequired = attemptNumber >= 3;
  const notesRequired = attemptNumber >= 3;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, fontFamily: theme.font.family,
    }}>
      <div style={{
        backgroundColor: theme.colors.white,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.lg,
        width: '90%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }}>
        <h2 style={{ margin: `0 0 ${theme.spacing.lg}px 0`, fontSize: 18, fontWeight: 600, color: theme.colors.navy }}>
          Neuanalyse anfordern
          {attemptNumber > 1 && (
            <span style={{ fontSize: 12, fontWeight: 400, color: theme.colors.navyMid, marginLeft: 8 }}>
              (Versuch {attemptNumber})
            </span>
          )}
        </h2>

        {requirementHint && (
          <div style={{
            padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
            marginBottom: theme.spacing.md,
            backgroundColor: theme.colors.bgLight,
            borderRadius: theme.borderRadius.md,
            fontSize: 12, color: theme.colors.navyMid,
            border: `1px solid ${theme.colors.borderLight}`,
          }}>
            {requirementHint}
          </div>
        )}

        <div style={{ marginBottom: theme.spacing.md }}>
          <label htmlFor="reanalysis-grund" style={{ display: 'block', marginBottom: theme.spacing.xs, fontWeight: 500, fontSize: 13, color: theme.colors.navyMid }}>
            Grund der Ablehnung{labelSuffix(reasonRequired)}
          </label>
          <select
            id="reanalysis-grund"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            style={{
              width: '100%', padding: `${theme.spacing.sm + 2}px`, borderRadius: theme.borderRadius.md,
              border: `1px solid ${theme.colors.borderLight}`, fontSize: 13, fontFamily: theme.font.family,
              color: theme.colors.navyMid, outline: 'none',
            }}
          >
            <option value="">-- Bitte auswählen --</option>
            <option value="wrong_category">{`Falsche ${cm.categoryNoun}`}</option>
            <option value="wrong_response">Falsche Antwort</option>
            <option value="incomplete">Unvollständig</option>
            <option value="tone_issue">Tonalität nicht passend</option>
            <option value="other">Sonstiges</option>
          </select>
        </div>

        <div style={{ marginBottom: theme.spacing.sm }}>
          <label style={{ display: 'block', marginBottom: theme.spacing.xs, fontWeight: 500, fontSize: 13, color: theme.colors.navyMid }}>
            Vorgeschlagene {cm.categoryNoun}{labelSuffix(categoryRequired)}
          </label>
          <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
            {categoryOptions.map(({ id, label }) => {
              const isActive = suggestedCategory === id;
              const isCurrent = id === currentCategory;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSuggestedCategory(isActive ? '' : id)}
                  style={{
                    padding: `${theme.spacing.xs + 2}px ${theme.spacing.md}px`,
                    borderRadius: theme.borderRadius.md,
                    border: isActive ? `2px solid ${theme.colors.navy}` : `1px solid ${theme.colors.borderLight}`,
                    backgroundColor: isActive ? theme.colors.bgLight : theme.colors.white,
                    color: isActive ? theme.colors.navy : theme.colors.navyMid,
                    cursor: 'pointer',
                    fontWeight: isCurrent ? 700 : 400,
                    fontSize: 13, fontFamily: theme.font.family,
                  }}
                >
                  {label}{isCurrent ? ' (aktuell)' : ''}
                </button>
              );
            })}
          </div>
        </div>

        {hasCategory && (
          <div style={{ marginBottom: theme.spacing.md }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
              fontSize: 13, color: theme.colors.navyMid, cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={forceCategory}
                onChange={(e) => setForceCategory(e.target.checked)}
                style={{ accentColor: theme.colors.navy }}
              />
              {cm.categoryNoun} vorgeben (KI-Klassifizierung überspringen)
            </label>
          </div>
        )}

        {/* Anhänge & OCR — collapsible */}
        {attachments && attachments.length > 0 ? (
          <div style={{ marginBottom: theme.spacing.md }}>
            <button
              type="button"
              onClick={() => setOcrExpanded(p => !p)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontWeight: 500, fontSize: 13, color: theme.colors.navyMid, fontFamily: theme.font.family,
                display: 'flex', alignItems: 'center', gap: theme.spacing.xs,
              }}
            >
              <span style={{
                display: 'inline-block', transition: 'transform 0.15s ease',
                transform: ocrExpanded ? 'rotate(90deg)' : 'rotate(0deg)', fontSize: 10,
              }}>
                &#9654;
              </span>
              {/* Bare title while cm.configLoading, exactly as TicketDetail's Anhänge PanelSection
                  does: the count depends on the tenant's counting rule, so painting the inclusive
                  number first and correcting it on settle is a content shift the settle has to
                  undo — and it would move every control below this collapsible. */}
              Anhänge &amp; OCR{cm.configLoading ? '' : ` (${anhaengeCount})`}
            </button>
            {ocrExpanded && (
              <div style={{
                marginTop: theme.spacing.sm, display: 'flex', flexDirection: 'column', gap: theme.spacing.sm,
                padding: theme.spacing.sm, backgroundColor: theme.colors.bgPage,
                borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.borderLight}`,
              }}>
                {cm.configLoading ? (
                  // Row COUNT is known before the flag is (attachments arrived with the ticket) —
                  // only the grouping and the number depend on config, so one shimmer per known row
                  // holds the settled height from the first paint. Height 20, not TicketDetail's 16:
                  // these rows carry a TriStateControl, which is taller than a text-only row.
                  anhaengeRows.map((row, i) => (
                    <SkeletonBlock key={`${row.name}#${i}`} height={20} />
                  ))
                ) : (
                  <>
                    {anhaengeVisibleRows.map(renderAnhangRow)}
                    {/* Flag OFF only: documents render above, inline rows stay controllable below the
                        subheader — never dropped. Row order (documents-then-inline) is never disturbed
                        by re-concatenation; anhaengeVisibleRows is documents-only in this posture. */}
                    {!attachmentsCountInline && anhaengeInline.length > 0 && (
                      <>
                        <div style={{
                          fontSize: 10, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase',
                          color: theme.colors.textSecondary, marginTop: theme.spacing.sm,
                        }}>
                          {ocrReasonGroupHeading('inline')}
                        </div>
                        {anhaengeInline.map(renderAnhangRow)}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginBottom: theme.spacing.md }}>
            <span style={{ fontSize: 13, color: theme.colors.textSecondary }}>
              Keine Anhänge — OCR-Optionen nicht verfügbar
            </span>
          </div>
        )}

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{ display: 'block', marginBottom: theme.spacing.xs, fontWeight: 500, fontSize: 13, color: theme.colors.navyMid }}>
            Anmerkungen{labelSuffix(notesRequired)}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Zusätzliche Hinweise für die Neuanalyse..."
            rows={3}
            style={{
              width: '100%', padding: theme.spacing.sm, borderRadius: theme.borderRadius.md,
              border: `1px solid ${theme.colors.borderLight}`, fontSize: 13, fontFamily: theme.font.family,
              resize: 'vertical', boxSizing: 'border-box', outline: 'none',
              color: theme.colors.navyMid,
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: theme.spacing.sm, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              padding: `${theme.spacing.sm + 2}px ${theme.spacing.lg}px`,
              borderRadius: theme.borderRadius.md,
              border: `1px solid ${theme.colors.borderLight}`,
              backgroundColor: theme.colors.white,
              color: theme.colors.navyMid,
              cursor: 'pointer', fontSize: 13, fontFamily: theme.font.family,
              transition: 'opacity 0.15s ease',
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !meetsRequirements}
            style={{
              padding: `${theme.spacing.sm + 2}px ${theme.spacing.lg}px`,
              borderRadius: theme.borderRadius.md,
              border: 'none',
              backgroundColor: theme.colors.error,
              color: theme.colors.white,
              cursor: (isSubmitting || !meetsRequirements) ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 500, fontFamily: theme.font.family,
              opacity: (isSubmitting || !meetsRequirements) ? 0.6 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            {isSubmitting ? 'Wird gesendet...' : 'Neu analysieren'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReanalysisModal;
